import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAttemptReview } from "@/hooks/useExam";
import { getMyAccessStatus } from "@/api/subscriptions";
import { useAuthStore } from "@/stores/authStore";
import ExamResultsComponent from "@/components/exam/ExamResults";
import type { CategoryScore } from "@/types";

export default function ExamResultsPage() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const { isAuthenticated } = useAuthStore();
  const { data: review, isLoading, error } = useAttemptReview(attemptId || "");
  const { data: accessStatus } = useQuery({
    queryKey: ["access-status"],
    queryFn: getMyAccessStatus,
    enabled: isAuthenticated,
  });

  const needsSubscription =
    accessStatus?.has_used_free_trial && !accessStatus?.has_active_subscription;

  const categoryScores = useMemo<CategoryScore[]>(() => {
    if (!review) return [];
    const map = new Map<string, { id: string; name: string; correct: number; total: number }>();

    review.questions.forEach((q) => {
      const catId = q.category_id;
      const catName = q.category_name;
      const existing = map.get(catId) || { id: catId, name: catName, correct: 0, total: 0 };
      existing.total++;
      if (q.is_correct) existing.correct++;
      map.set(catId, existing);
    });

    return Array.from(map.values()).map((c) => ({
      category_id: c.id,
      category_name: c.name,
      correct: c.correct,
      total: c.total,
      percentage: c.total > 0 ? (c.correct / c.total) * 100 : 0,
    }));
  }, [review]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <svg className="animate-spin h-8 w-8 text-primary-600" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (error || !review) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <p className="text-danger-600">Failed to load exam results.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
        <ExamResultsComponent
          attempt={review.attempt}
          categoryScores={categoryScores}
          passMark={review.pass_mark}
          needsSubscription={needsSubscription}
        />
      </div>
    </div>
  );
}
